import React, { Fragment, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Menu, Transition } from '@headlessui/react'
import { ChevronDown, LogOut, User, Lock } from 'react-feather';
import { useAuth } from 'core/contexts/auth/AuthContext'
import AvatarDefault from 'assets/images/avatar_default.png';
import ChangePasswordModal from 'pages/resetPassword/ChangePasswordModal';
import palette from 'core/constant/palette';

/**
 * The header's account control: the avatar-and-name button, its dropdown
 * (account details, change password, sign out) and the change-password modal it
 * opens. Only rendered when signed in ({user && <AccountMenu />} in NavBar);
 * it reads the current user from the auth context itself. Lifted out of NavBar
 * unchanged (BLD-11).
 */
const AccountMenu = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [isShowChangePassModal, setIsShowChangePassModal] = useState<boolean>(false);

    const handleAvatarOnError = (e: any) => {
        e.target.src = AvatarDefault;
    };

    return (
        <>
            <Menu as="div" className="relative inline-block outline-none">
                {/* `outline-none` with nothing in its place: tabbing to the
                    account menu left no visible mark anywhere on screen. */}
                <Menu.Button className='rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-gold'>
                    <div className='flex items-center gap-3'>
                        <div className='w-[24px] h-[24px] rounded-full border-[2px] border-secondary flex items-center justify-center'>
                            {user?.Avatar &&
                                <img
                                src={AvatarDefault}
                                alt="Avatar"
                                className='w-[22px] h-[22px] rounded-full'
                                onError={handleAvatarOnError}
                                />
                            }
                        </div>
                        <div>{`${user?.LastName} ${user?.FirstName}`}</div>
                        <div className='mt-[2px]'>
                            <ChevronDown size={24} color={palette.gold}/>
                        </div>
                    </div>
                </Menu.Button>
                <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    {/* Three `li`s with onClick handlers inside a plain `div`:
                        the account menu could be opened from the keyboard and
                        then not used from it. As `Menu.Item` buttons they get
                        arrow keys, Escape, and the roles that say "menu".
                        The rows already had a 48px height from `py-3`. */}
                    <Menu.Items className='absolute right-0 top-[35px] w-[209px] bg-primary border border-secondary rounded-md focus:outline-none overflow-hidden'>
                        <Menu.Item>
                        {({ active }: { active: boolean }) => (
                            <button
                            type='button'
                            className={`w-full text-left cursor-pointer ${active ? 'bg-secondary/20' : ''}`}
                            onClick={() => {
                                navigate('/thong-tin-tai-khoan');
                            }}
                            >
                                <div className='flex items-center gap-2 py-3 px-4'>
                                    <User size={24} color={palette.gold}/>
                                    <span className="text-sm flex-1">Thông tin tài khoản</span>
                                </div>
                            </button>
                        )}
                        </Menu.Item>
                        <Menu.Item>
                        {({ active }: { active: boolean }) => (
                            <button
                            type='button'
                            className={`w-full text-left cursor-pointer ${active ? 'bg-secondary/20' : ''}`}
                            onClick={() => {
                                setIsShowChangePassModal(true);
                            }}
                            >
                                <div className='flex items-center gap-2 py-3 px-4'>
                                    <Lock size={24} color={palette.gold}/>
                                    <span className="text-sm flex-1">Đổi mật khẩu</span>
                                </div>
                            </button>
                        )}
                        </Menu.Item>
                        <Menu.Item>
                        {({ active }: { active: boolean }) => (
                            <button
                            type='button'
                            className={`w-full text-left cursor-pointer ${active ? 'bg-secondary/20' : ''}`}
                            onClick={() => {
                                logout();
                            }}
                            >
                                <div className='flex items-center gap-2 py-3 px-4'>
                                    <LogOut size={24} color={palette.gold}/>
                                    <span className="text-sm flex-1">Đăng xuất</span>
                                </div>
                            </button>
                        )}
                        </Menu.Item>
                    </Menu.Items>
                </Transition>
            </Menu>
            <ChangePasswordModal
            isOpen={isShowChangePassModal}
            closeModal={() => setIsShowChangePassModal(false)}
            modalClass=' w-[40vw]'
            />
        </>
    )
}

export default AccountMenu
